/**
 * Agent Service - API-first agent data access.
 *
 * Production behavior:
 * - Always fetches from runtime-api
 * - Errors propagate to components for proper error UI
 * - No silent fallbacks to stale data
 * - No in-memory cache — React Query handles caching at the hook layer
 *
 * Development behavior (VITE_USE_AGENT_API=false):
 * - Uses hardcoded agents.config.ts for offline development
 *
 * The service maps between ResolvedAgent (API) and legacy Agent type
 * used by existing components, enabling gradual migration.
 */
import { apiClient } from './api-client';
import { Agent, AgentType } from './agents.config';
import { ResolvedAgent } from '@/types';

// Feature flag: when false, use local config for offline development
const USE_API = import.meta.env.VITE_USE_AGENT_API !== 'false';

class AgentService {
  private static instance: AgentService;

  static getInstance(): AgentService {
    if (!AgentService.instance) {
      AgentService.instance = new AgentService();
    }
    return AgentService.instance;
  }

  /**
   * Map ResolvedAgent to legacy Agent format.
   * Now uses all UI metadata from the API response.
   */
  private mapResolvedToLegacy(resolved: ResolvedAgent): Agent {
    // Use API color (now authoritative)
    const themeColor = resolved.theme_color || '#6366F1';

    // Use slug from API, fallback to ID
    const slug = resolved.slug || resolved.id;

    // Map channel configs from API, fall back to simple string mapping
    const channels = resolved.channel_configs?.length > 0
      ? resolved.channel_configs.map((ch) => ({
          id: ch.id,
          name: ch.name,
          type: ch.type as 'internal' | 'external',
          platform: ch.platform as any,
          enabled: ch.enabled,
        }))
      : resolved.channels.map((ch) => ({
          id: ch,
          name: ch,
          type: 'external' as const,
          platform: 'api' as any,
          enabled: true,
        }));

    // Map reporting schedule from API
    const reportingSchedule = resolved.reporting_schedule
      ? {
          frequency: resolved.reporting_schedule.frequency as any,
          reportsTo: resolved.reporting_schedule.reports_to,
          priority: resolved.reporting_schedule.priority as any,
        }
      : {
          frequency: 'daily' as const,
          reportsTo: [],
          priority: 'medium' as const,
        };

    return {
      id: resolved.id,
      name: resolved.name,
      customName: resolved.name,
      avatar: resolved.avatar_url || `/agent_profile_pictures/${slug}.webp`,
      type: resolved.type as AgentType,
      themeColor,
      icon: resolved.icon,
      description: resolved.description,
      slug,
      requirements: resolved.requirements || [],
      outputs: resolved.outputs || [],
      dependencies: resolved.dependencies || [],
      frontendComponents: resolved.frontend_components || [],
      channels,
      config: {
        model: resolved.default_model || 'claude-3-sonnet',
        prompt: resolved.system_prompt,
        tools: resolved.tools,
        reportingSchedule,
        theme: themeColor,
        profilePicture: resolved.avatar_url || '',
        customName: resolved.name,
      },
      // Legacy flat string list — kept for backwards compat with components
      // that still treat tabs as opaque IDs.
      tabs: resolved.custom_tabs?.map((t) => t.id),
      // Backend-driven structured tabs (Task 075 §C2). Each entry preserves
      // `app_id` so capability-driven filtering can run downstream.
      customTabs: resolved.custom_tabs?.map((t) => ({
        id: t.id,
        label: t.label,
        app_id: (t as { app_id?: string | null }).app_id ?? null,
      })),
    };
  }

  /**
   * Fetch agents from API.
   * No caching — React Query handles that.
   */
  private async fetchFromApi(): Promise<{ agents: Agent[]; resolved: ResolvedAgent[] }> {
    const response = await apiClient.runtimeAgents.list();
    const agents = response.agents.map(this.mapResolvedToLegacy);
    return { agents, resolved: response.agents };
  }

  /**
   * Load agents from hardcoded config (development only).
   */
  private async loadFromConfig(): Promise<Agent[]> {
    const { agents } = await import('./agents.config');
    return agents;
  }

  /**
   * Get all agents (legacy format).
   * Throws on API error — components should handle with error boundaries or try/catch.
   */
  async getAllAgents(): Promise<Agent[]> {
    if (!USE_API) return this.loadFromConfig();
    const { agents } = await this.fetchFromApi();
    return agents;
  }

  /**
   * Get all resolved agents (new format).
   * Throws on API error — components should handle with error boundaries or try/catch.
   */
  async getAllResolvedAgents(): Promise<ResolvedAgent[]> {
    if (!USE_API) {
      const agents = await this.loadFromConfig();
      return agents.map((agent) => ({
        id: agent.id,
        name: agent.customName || agent.name,
        type: agent.type,
        description: agent.description || '',
        slug: agent.slug,
        system_prompt: agent.config.prompt,
        tools: agent.config.tools,
        channels: agent.channels.map((c) => c.id),
        brain_type: 'focal',
        brain_config: {},
        default_model: agent.config.model,
        theme_color: agent.themeColor,
        icon: agent.icon,
        avatar_url: agent.avatar,
        requirements: agent.requirements,
        outputs: agent.outputs,
        dependencies: agent.dependencies,
        frontend_components: agent.frontendComponents,
        channel_configs: agent.channels,
        reporting_schedule: agent.config.reportingSchedule ? {
          frequency: agent.config.reportingSchedule.frequency,
          reports_to: agent.config.reportingSchedule.reportsTo,
          priority: agent.config.reportingSchedule.priority,
        } : null,
        custom_tabs:
          agent.customTabs?.map((t) => ({
            id: t.id,
            label: t.label,
            app_id: t.app_id ?? null,
          })) ||
          agent.tabs?.map((id) => ({ id, label: id, app_id: null })) ||
          [],
        enabled: true,
        is_custom: false,
        template_id: agent.id,
        has_overrides: false,
      }));
    }

    const response = await apiClient.runtimeAgents.list();
    return response.agents;
  }

  /**
   * Get agent by ID.
   */
  async getAgentById(id: string): Promise<Agent | undefined> {
    const agents = await this.getAllAgents();
    return agents.find((a) => a.id === id);
  }

  /**
   * Get agent by slug.
   */
  async getAgentBySlug(slug: string): Promise<Agent | undefined> {
    const agents = await this.getAllAgents();
    return agents.find((a) => a.slug === slug);
  }

  /**
   * Get agents by type.
   */
  async getAgentsByType(type: AgentType): Promise<Agent[]> {
    const agents = await this.getAllAgents();
    return agents.filter((agent) => agent.type === type);
  }

  /**
   * Preload agents for faster navigation.
   */
  async preloadAgents(): Promise<void> {
    await this.getAllAgents();
  }

  /**
   * No-op for backwards compatibility.
   * React Query handles cache invalidation via queryClient.invalidateQueries().
   */
  clearCache(): void {
    // No-op — React Query is the single cache layer
  }

  /**
   * Refresh agents.
   */
  async refresh(): Promise<Agent[]> {
    return this.getAllAgents();
  }
}

export const agentService = AgentService.getInstance();
